import React from 'react';

import ChapterCustomExamPopUp from '../../../containers/chapterCustomExamPopUpContainer';
import history from '../../../utils/history';
import { setSessionStorageItem } from '../../../utils/sessionStorage';
import CircularLoading from '../../Common/CircularLoading/CircularLoading';
import PfeSktSwitch from '../../Common/PfeSktSwitch/PfeSktSwitch';
import StudyList from '../../StudyList/StudyList';
import ChapterStudyPlanModal from './ChapterStudyPlanModal';
import ChapterStudyPlanView from './ChapterStudyPlanView';
import PersonalizedChapterSelectorModal from './PersonalizedChapterSelectorModal';

class PersonalizedStudyView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isModalOpen: false,
      isStudyPlanModalOpen: false,
      isCustomExamModalOpen: false,
      selectedData: this.computeSelectedData(props.availableChapters),
    };
  }

  computeSelectedData(availableChapters) {
    if (!availableChapters) return null;
    return {
      ...availableChapters,
      references: (availableChapters.references || [])
        .map((ref) => {
          const chapters = (ref.chapters || []).filter((ch) => ch.isSelected);
          return {
            ...ref,
            chapters,
            new: chapters.reduce((sum, ch) => sum + Number(ch.new || 0), 0),
            learning: chapters.reduce(
              (sum, ch) => sum + Number(ch.learning || 0),
              0,
            ),
            review: chapters.reduce(
              (sum, ch) => sum + Number(ch.review || 0),
              0,
            ),
          };
        })
        .filter((ref) => ref.chapters.length > 0),
    };
  }

  getActiveTab = () => {
    const { pfeSktId, user } = this.props;
    if (user?.rate?.isPfeOnly) return 'PFE';
    if (Number(pfeSktId) === Number(user?.rate?.id)) return 'SKT';
    return 'PFE';
  };

  handleTabSwitch = (tab) => {
    const { user, setSwitchPfeSkt } = this.props;
    if (!user) return;
    if (tab === 'SKT' && user.rate?.isPfeOnly) return;

    const newId = tab === 'SKT' ? user?.rate?.id : user?.pfeRate?.id;
    if (!newId) return;

    setSwitchPfeSkt(newId);
    this.props.getAvailableChapters(newId);
  };

  componentDidMount() {
    this.props.setBackTo('/app/personalized-study');

    const { availableChapters, pfeSktId } = this.props;

    if (pfeSktId) {
      this.props.getAvailableChapters(pfeSktId);
    }

    this.props.loadChapterStudyPlan();

    if (
      availableChapters !== undefined &&
      !this.hasSelectedChapters(availableChapters)
    ) {
      this.setState({ isModalOpen: true });
    }
  }

  componentDidUpdate(prevProps) {
    const prev = prevProps.availableChapters;
    const curr = this.props.availableChapters;
    const { user, pfeSktId } = this.props;

    if (prevProps.pdfLink !== this.props.pdfLink && this.props.pdfLink?.url) {
      window.open(this.props.pdfLink.url);
    }

    // Fetch once user data becomes available (e.g. after page reload)
    if (!prevProps.user?.rate?.id && user?.rate?.id && curr === undefined) {
      this.props.getAvailableChapters(pfeSktId);
    }

    // Re-fetch when the switch changes
    if (prevProps.pfeSktId !== pfeSktId && pfeSktId) {
      this.props.getAvailableChapters(pfeSktId);
      this.props.loadChapterStudyPlan();
    }

    if (
      prev === undefined &&
      curr !== undefined &&
      !this.hasSelectedChapters(curr)
    ) {
      this.setState({ isModalOpen: true });
    }

    if (prevProps.saveChaptersLoading && !this.props.saveChaptersLoading) {
      this.setState({ isModalOpen: false });
    }

    if (prev !== curr) {
      this.setState({ selectedData: this.computeSelectedData(curr) });
    }
  }

  hasSelectedChapters(data) {
    return (data?.references || []).some((ref) =>
      (ref.chapters || []).some((ch) => ch.isSelected),
    );
  }

  setQuestionNotification = (id) => {
    this.props.setQuestionNotification(id, 'chapter');
  };

  renderEmptyState = () => (
    <div className="empty-state">
      <div className="empty-state__card">
        <header className="empty-state__header">
          <h2 className="empty-state__title">No chapters selected yet</h2>
          <p className="empty-state__subtitle">
            Add chapters to your personal study list to see them here.
          </p>
        </header>
      </div>
    </div>
  );

  render() {
    const { availableChapters, chapterStudyPlan, userSubscription, user } =
      this.props;
    const { selectedData } = this.state;
    const isLoading = availableChapters === undefined;
    const hasChapters = !isLoading && selectedData?.references?.length > 0;
    const hasPlan =
      chapterStudyPlan &&
      (chapterStudyPlan.this_week_focus ||
        chapterStudyPlan.upcoming?.length > 0 ||
        chapterStudyPlan.completed?.length > 0);
    const activeTab = this.getActiveTab();
    const sktLocked = userSubscription
      ? !userSubscription.isOccupational || user?.rate?.isPfeOnly
      : false;

    return (
      <div className="chapter-list-view mt-4">
        <div className="d-flex justify-content-end px-3 mb-3">
          <PfeSktSwitch
            activeTab={activeTab}
            onSwitch={this.handleTabSwitch}
            variant="toggle"
            size="md"
            sktLocked={sktLocked}
          />
        </div>
        {!isLoading && (
          <div className="d-flex justify-content-between align-items-center px-3 mb-3 chapter-list-view__toolbar">
            {hasChapters ? (
              <div className="d-flex" style={{ gap: '0.5rem' }}>
                <button
                  type="button"
                  className="chapter-list-view__btn"
                  onClick={() => {
                    this.props.setBackTo('/app/personalized-study');
                    history.push('/app/question?isPersonalizedStudyAll');
                  }}
                >
                  STUDY ALL
                </button>
                {activeTab === 'SKT' && (
                  <button
                    type="button"
                    className="chapter-list-view__btn"
                    onClick={() =>
                      this.setState({ isCustomExamModalOpen: true })
                    }
                  >
                    CUSTOM PRACTICE EXAM
                  </button>
                )}

                <button
                  type="button"
                  className="chapter-list-view__btn"
                  onClick={() => {
                    setSessionStorageItem('fromPersonalizedStudy', 'true');
                    history.push('/app/audio-study');
                  }}
                >
                  CUSTOM AUDIO PLAYLIST
                </button>
                {!hasPlan && (
                  <button
                    type="button"
                    className="chapter-list-view__btn"
                    onClick={() =>
                      this.setState({ isStudyPlanModalOpen: true })
                    }
                  >
                    CREATE STUDY PLAN
                  </button>
                )}
              </div>
            ) : (
              <div />
            )}
            <button
              type="button"
              className="chapter-list-view__btn"
              onClick={() => this.setState({ isModalOpen: true })}
            >
              {hasChapters ? 'EDIT CHAPTERS' : 'SELECT CHAPTERS'}
            </button>
          </div>
        )}

        {isLoading && <CircularLoading />}
        {!isLoading && !hasChapters && this.renderEmptyState()}
        {hasChapters && (
          <StudyList
            user={user}
            userSubscription={userSubscription}
            setQuestionNotification={this.setQuestionNotification}
            items={selectedData}
            location={this.props.location}
            setChapterId={this.props.setChapterId}
            getPdfByReference={this.props.getPdfByReference}
            activateSubscription={this.props.activateSubscription}
            subscriptionActivationSucces={
              this.props.subscriptionActivationSucces
            }
            subscriptionActivationWaiting={
              this.props.subscriptionActivationWaiting
            }
            subscriptionActivationMessage={
              this.props.subscriptionActivationMessage
            }
            pdfLink={this.props.pdfLink?.url}
            setIsStudyingSubmittedQuestions={
              this.props.setIsStudyingSubmittedQuestions
            }
            setIsStudyingFavoriteQuestions={
              this.props.setIsStudyingFavoriteQuestions
            }
            setIsStudyingMyFavoriteQuestions={
              this.props.setIsStudyingMyFavoriteQuestions
            }
            setIsStudyingMySubmittedQuestions={
              this.props.setIsStudyingMySubmittedQuestions
            }
            updateDisabledChapter={this.props.updateDisabledChapter}
            getAudioPlaylist={this.props.getAudioPlaylist}
            audioPlaylist={this.props.audioPlaylist}
            audioPlaylistsProgress={this.props.audioPlaylistsProgress}
            getAudioPlaylistsProgress={this.props.getAudioPlaylistsProgress}
            updateAudioPlaylistProgress={this.props.updateAudioPlaylistProgress}
          />
        )}

        {hasPlan && (
          <ChapterStudyPlanView
            chapterStudyPlan={chapterStudyPlan}
            userSubscription={userSubscription}
            setChapterId={this.props.setChapterId}
            getPdfByReference={this.props.getPdfByReference}
            onRebuild={() => this.setState({ isStudyPlanModalOpen: true })}
          />
        )}

        <PersonalizedChapterSelectorModal
          isOpen={this.state.isModalOpen}
          onClose={() => this.setState({ isModalOpen: false })}
          referenceData={availableChapters}
          isLoading={this.props.availableChaptersLoading}
          isSaving={this.props.saveChaptersLoading}
          saveSelectedChapters={this.props.saveSelectedChapters}
        />

        <ChapterStudyPlanModal
          isOpen={this.state.isStudyPlanModalOpen}
          onClose={() => this.setState({ isStudyPlanModalOpen: false })}
          buildChapterStudyPlan={this.props.buildChapterStudyPlan}
          buildChapterStudyPlanLoading={this.props.buildChapterStudyPlanLoading}
        />
        {this.state.isCustomExamModalOpen && (
          <ChapterCustomExamPopUp
            isOpen={this.state.isCustomExamModalOpen}
            toggle={() => this.setState({ isCustomExamModalOpen: false })}
            chapterSelection={selectedData?.references}
            isSaving={this.props.saveChaptersLoading}
            saveSelectedChapters={this.props.saveSelectedChapters}
          />
        )}
      </div>
    );
  }
}

export default PersonalizedStudyView;
